import { useState } from "react";
import { Button } from "reactstrap";
import BrieIconOutlined from "../../../components/BrieChatMenu/BrieIconOutlined";
import useTranscript from "../../../api/api-hooks/tools/useTranscript";
import AudioRecorder from "../../../components/Sidebar/QuickNote/QuickNoteRecording/AudioRecordingUtils";
import { IsMobileAtom } from "../../../App";

import AppSpinner from "../../../elements/AppSpinner";
import { useAtomValue, useSetAtom } from "jotai";
import { brieCommandTextAtom } from "../../../components/BrieChatMenu/BrieChatSearchGPT";
import { brieMenuOpenAtom } from "../../../components/BrieChatMenu/BrieChatMenuButton";

import classNames from "classnames";
import { AppConfirm } from "../../../utils/PopupUtil";

const Recorder = new AudioRecorder();

export default function BrieVoiceCommands() {
  const [isRecording, setIsRecording] = useState(false);
  const [processing, setProcessing] = useState(false);

  const setCommandText = useSetAtom(brieCommandTextAtom);
  const setBrieMenuOpen = useSetAtom(brieMenuOpenAtom);
  const smallScreen = useAtomValue(IsMobileAtom);

  const { fetchTranscript, deleteTranscript } = useTranscript();

  return (
    <div className="position-relative">
      <Button
        disabled={processing}
        onClick={!isRecording ? onStartRecording : onFinishRecording}
        style={{
          width: smallScreen ? 36 : undefined,
          height: smallScreen ? 36 : undefined,
        }}
        outline={smallScreen}
        className={
          !smallScreen
            ? "p-0 px-1 mr--2 ml--1"
            : "d-flex justify-content-center align-items-center"
        }
        size="sm"
      >
        {!isRecording ? (
          !processing ? (
            <BrieIconOutlined
              transform={smallScreen ? "scale(1.3)" : undefined}
            />
          ) : (
            <AppSpinner style={{ width: 18, height: 18, borderWidth: 3 }} />
          )
        ) : (
          <div className="flex h-full py-1">
            <i
              className={classNames(
                "bg-primary text-white text-sm mdi mdi-pause"
              )}
              style={{
                padding: "2px 5px",
                borderRadius: "50%",
                marginRight: smallScreen ? "0rem" : "0.25rem",
              }}
              id={isRecording ? "ripple-animation-play-button" : ""}
            />
            <span
              style={{
                display: smallScreen ? "none" : "inline",
              }}
            >
              Brie is Listening...
            </span>
          </div>
        )}
      </Button>
    </div>
  );

  function restart() {
    setIsRecording(false);
    Recorder.cancelRecording();
  }

  async function onStartRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Don't forget to stop the stream after getting the permission
      stream.getTracks().forEach((track) => track.stop());

      // Check if the browser supports microphone access
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        showError("Your browser doesn't support microphone access.");
      }

      setIsRecording(true);
      Recorder.startRecording({
        setIsRecording,
        restart,
        onSuccess: (audio) => {
          onFetchTranscript(audio);
        },
      });
    } catch (err: any) {
      if (err.name === "NotAllowedError") {
        showError(
          "Microphone access was denied. Please enable it in your browser settings."
        );
      } else if (err.name === "NotFoundError") {
        showError("No microphone found. Please connect a microphone.");
      } else {
        showError(
          "An unexpected error occurred while trying to access your microphone."
        );
      }
    }
  }

  async function onFinishRecording() {
    setIsRecording(false);
    Recorder.finishRecording({});
  }

  async function onFetchTranscript(audioBlob: Blob) {
    setProcessing(true);

    try {
      const transcriptFileData = await fetchTranscript({
        audioBlob,
        onTranscriptSuccess: (transcript) => processRequest(transcript),
        onError: (fileData) => {
          showError("Something went wrong...");
          deleteTranscript({ fileData });
        },
      });

      deleteTranscript({
        fileData: transcriptFileData,
      });
    } catch (err) {
      showError("Transcription error. Please try again.");
    } finally {
      setProcessing(false);
    }
  }

  async function processRequest(request: string) {
    setProcessing(false);
    setBrieMenuOpen(true);
    setCommandText(request);
  }

  function showError(message: string) {
    AppConfirm({
      title: "Error",
      text: message,
      showCancelButton: false,
      confirmButtonColor: "var(--primary)",
      confirmButtonText: "OK",
    }).then(() => {
      restart();
    });
  }
}
